import { useEffect } from 'react'
import type { Theme } from '@/types'
import { applyFontPreset } from '@/lib/fonts'

// ============================================================================
// Convert hex color to space-separated RGB channels (for Tailwind alpha)
// e.g. "#8B5E3C" -> "139 94 60"
// ============================================================================

function hexToRgbChannels(hex: string): string {
  const clean = hex.replace('#', '')
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean

  const r = parseInt(full.substring(0, 2), 16)
  const g = parseInt(full.substring(2, 4), 16)
  const b = parseInt(full.substring(4, 6), 16)

  return `${r} ${g} ${b}`
}

// ============================================================================
// Apply theme CSS variables
// ============================================================================

function applyTheme(theme: Theme): void {
  const root = document.documentElement

  // Set CSS custom properties (RGB channels for Tailwind alpha support)
  root.style.setProperty('--color-primary', hexToRgbChannels(theme.primary))
  root.style.setProperty('--color-secondary', hexToRgbChannels(theme.secondary))
  root.style.setProperty('--color-accent', hexToRgbChannels(theme.accent))
  root.style.setProperty('--color-background', hexToRgbChannels(theme.background))

  // Set border radius
  const radiusValue = theme.radius === 'rounded' ? '12px' : '4px'
  root.style.setProperty('--radius-card', radiusValue)
  root.style.setProperty('--radius-button', theme.radius === 'rounded' ? '8px' : '4px')

  // Apply typography preset (config-driven via theme.font)
  applyFontPreset(theme.font)
}

// ============================================================================
// Theme Provider Component
// ============================================================================

interface ThemeProviderProps {
  theme: Theme
  children: React.ReactNode
}

export function ThemeProvider({ theme, children }: ThemeProviderProps) {
  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  // Handle dark mode
  useEffect(() => {
    const root = document.documentElement

    if (theme.mode === 'dark') {
      root.classList.add('dark')
    } else if (theme.mode === 'light') {
      root.classList.remove('dark')
    } else if (theme.mode === 'auto') {
      // Auto mode - check system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      if (prefersDark) {
        root.classList.add('dark')
      } else {
        root.classList.remove('dark')
      }
    }

    // Listen for system preference changes if in auto mode
    if (theme.mode === 'auto' && theme.darkMode === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const handleChange = (e: MediaQueryListEvent) => {
        if (e.matches) {
          root.classList.add('dark')
        } else {
          root.classList.remove('dark')
        }
      }

      mediaQuery.addEventListener('change', handleChange)
      return () => {
        mediaQuery.removeEventListener('change', handleChange)
      }
    }
  }, [theme])

  return <>{children}</>
}
